// Removed unused import

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';

export interface LocationCheckResponse {
  is_within_nyc: boolean;
  can_vote: boolean;
  message: string;
  area_hierarchy?: {
    block_group_id: string;
    neighborhood_id: string;
    borough_id: string;
    city_id: string;
  };
}

export interface VoteResponse {
  success: boolean;
  message: string;
  can_vote?: boolean;
  reason?: string;
  next_vote_time?: string;
  last_vote_time?: string;
}

export interface VoteStatusResponse {
  has_voted: boolean;
  can_vote: boolean;
  has_voted_last_24h: boolean;
  can_see_map: boolean;
  message?: string;
  current_hour?: string;
  next_vote_time?: string;
  last_vote_time?: string;
  last_vote?: {
    is_positive: boolean;
    neighborhood_name: string;
    created_at?: string;
  };
}

class VotingService {
  private sessionId: string | null = null;
  private readonly SESSION_COOKIE = 'hwa_session_id';
  private readonly SESSION_STORAGE = 'hwa_session_id';
  private readonly LAST_VOTE_COOKIE = 'hwa_last_vote_at';
  private readonly NEXT_VOTE_COOKIE = 'hwa_next_vote_at';

  constructor() {
    this.sessionId = this.getSessionId();
  }

  /**
   * Obtiene o crea un session ID único
   */
  private getSessionId(): string {
    let sessionId = this.getCookie(this.SESSION_COOKIE);
    if (!sessionId) {
      try {
        sessionId = localStorage.getItem(this.SESSION_STORAGE);
      } catch {
        sessionId = null;
      }
    }

    if (!sessionId) {
      sessionId = this.generateSessionId();
    }

    // Siempre re-sincronizar cookie + localStorage para máxima estabilidad de sesión.
    this.setCookie(this.SESSION_COOKIE, sessionId, 365); // 1 año
    try {
      localStorage.setItem(this.SESSION_STORAGE, sessionId);
    } catch {
      // Ignore storage errors (private mode/quota).
    }

    return sessionId;
  }

  /**
   * Genera un session ID único
   */
  private generateSessionId(): string {
    return 'hwa_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
  }

  /**
   * Obtiene una cookie por nombre
   */
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Establece una cookie
   */
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    // Añadir Secure en producción (HTTPS)
    const isSecure = window.location.protocol === 'https:' ? ';Secure' : '';
    
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${isSecure}`;
  }

  private syncVoteCookies(lastVoteTime?: string | null, nextVoteTime?: string | null): void {
    if (lastVoteTime) {
      this.setCookie(this.LAST_VOTE_COOKIE, lastVoteTime, 365);
    }
    if (nextVoteTime) {
      this.setCookie(this.NEXT_VOTE_COOKIE, nextVoteTime, 365);
    }
  }

  getLastVoteCookie(): string | null {
    return this.getCookie(this.LAST_VOTE_COOKIE);
  }

  getNextVoteCookie(): string | null {
    return this.getCookie(this.NEXT_VOTE_COOKIE);
  }

  /**
   * Obtiene la ubicación actual del usuario
   */
  async getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const requestLocation = () => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
          },
          (error) => {
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutos
          }
        );
      };

      if (navigator.permissions?.query) {
        navigator.permissions
          .query({ name: 'geolocation' as PermissionName })
          .then((status) => {
            if (status.state === 'denied') {
              reject(new Error('Geolocation permission denied'));
              return;
            }
            requestLocation();
          })
          .catch(() => {
            // Fallback for browsers where Permissions API is unreliable.
            requestLocation();
          });
        return;
      }

      requestLocation();
    });
  }

  /**
   * Verifica si la ubicación está dentro de NYC
   */
  async checkLocation(latitude: number, longitude: number): Promise<LocationCheckResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/voting/check-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          latitude,
          longitude
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const payload: LocationCheckResponse = await response.json();
      return payload;
    } catch (error) {
      console.error('Error checking location:', error);
      throw error;
    }
  }

  /**
   * Obtiene el estado de votación del usuario
   */
  async getVoteStatus(): Promise<VoteStatusResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/voting/vote-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': this.sessionId || '',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const payload: VoteStatusResponse = await response.json();
      this.syncVoteCookies(payload.last_vote_time || payload.last_vote?.created_at || null, payload.next_vote_time || null);
      return payload;
    } catch (error) {
      console.error('Error getting vote status:', error);
      throw error;
    }
  }

  /**
   * Registra un voto
   */
  async submitVote(latitude: number, longitude: number, isPositive: boolean): Promise<VoteResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/voting/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': this.sessionId || '',
        },
        body: JSON.stringify({
          latitude,
          longitude,
          is_positive: isPositive
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting vote:', error);
      throw error;
    }
  }

  /**
   * Verifica si el usuario puede votar (ubicación + estado de voto)
   */
  async canUserVote(): Promise<{
    canVote: boolean;
    reason?: string;
    message?: string;
    location?: { latitude: number; longitude: number };
    areaHierarchy?: any;
  }> {
    try {
      // Obtener ubicación
      const location = await this.getCurrentLocation();
      
      // Verificar ubicación
      const locationCheck = await this.checkLocation(location.latitude, location.longitude);
      
      if (!locationCheck.is_within_nyc) {
        return {
          canVote: false,
          reason: 'outside_nyc',
          message: locationCheck.message,
          location
        };
      }

      // Verificar estado de voto
      const voteStatus = await this.getVoteStatus();
      
      if (voteStatus.has_voted) {
        return {
          canVote: false,
          reason: 'already_voted',
          message: 'You have already voted during this hour',
          location,
          areaHierarchy: locationCheck.area_hierarchy
        };
      }

      return {
        canVote: true,
        location,
        areaHierarchy: locationCheck.area_hierarchy
      };

    } catch (error) {
      console.error('Error checking if user can vote:', error);
      const geolocationCode =
        typeof error === 'object' && error !== null && 'code' in error
          ? Number((error as { code?: unknown }).code)
          : null;
      const geolocationMessage =
        error instanceof Error ? error.message.toLowerCase() : '';
      const isLocationPermissionIssue =
        geolocationCode === 1 ||
        geolocationMessage.includes('permission denied') ||
        geolocationMessage.includes('geolocation permission denied');
      if (isLocationPermissionIssue) {
        return {
          canVote: false,
          reason: 'location_required',
          message: 'Location access is required for this app to work.'
        };
      }
      return {
        canVote: false,
        reason: 'error',
        message: 'Unable to verify voting eligibility'
      };
    }
  }

  /**
   * Obtiene el session ID actual
   */
  getCurrentSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Limpia la sesión (para testing)
   */
  clearSession(): void {
    this.setCookie(this.SESSION_COOKIE, '', -1);
    try {
      localStorage.removeItem(this.SESSION_STORAGE);
    } catch {
      // Ignore storage errors.
    }
    this.sessionId = null;
  }
}

export const votingService = new VotingService();
export default votingService;
